import { parametersStore } from '../parametersStore';
import { useProjectDetailsQuery, getProjectDetailsCached } from '../../api';
import { useCallback, useMemo } from 'react';
import { ProjectDetailsResponseDTO } from '../../api/project/project.types';

type BaseParams = {
  project_uuid: string;
  participation_uuid?: string;
  is_preview: boolean;
};

type ProjectParams = BaseParams & {
  auth?: string;
  token?: string;
  room_url?: string;
  share_id?: string;
  website_testing_url?: string;
  task_uuid?: string;
  response_uuid?: string;
  target_site_type?: string;
};

function mapProjectParams(data?: ProjectDetailsResponseDTO): ProjectParams | undefined {
  const nextTask = data?.tasks?.find((task) => task.task_uuid === data.next_task_uuid);
  const projectUuid = parametersStore.get('project_uuid') || undefined;
  const participationUuid = parametersStore.get('participation_uuid') || undefined;
  const isPreview = parametersStore.get('is_preview') === '1' || !participationUuid;
  const authTokenParam = parametersStore.get('auth') || undefined;

  if (!data && projectUuid) {
    return {
      participation_uuid: participationUuid,
      project_uuid: projectUuid,
      is_preview: isPreview,
    };
  }

  if (!data) {
    return undefined;
  }

  return {
    auth: isPreview ? authTokenParam : data.auth_token || undefined,
    token: data.recording?.token,
    room_url: data.recording?.room_url,
    share_id: data?.share_id,
    is_preview: isPreview,
    website_testing_url: nextTask?.website_testing_url,
    task_uuid: nextTask?.task_uuid,
    participation_uuid: participationUuid,
    project_uuid: data.project_uuid,
    response_uuid: nextTask?.response_uuid,
    target_site_type: data.target_site_type,
  };
}

const useProjectParams = () => {
  const projectUuid = parametersStore.get('project_uuid') || undefined;
  const participationUuid = parametersStore.get('participation_uuid') || undefined;
  const isPreview = parametersStore.get('is_preview') === '1' || !participationUuid;

  const { data, isLoading, ...queryResult } = useProjectDetailsQuery({
    projectUuid,
    participationUuid,
    isPreview,
  });

  const mappedData: ProjectParams | undefined = useMemo(() => {
    return mapProjectParams(data);
  }, [data]);

  const getParam = useCallback(
    <K extends keyof ProjectParams>(param: K): ProjectParams[K] | undefined => {
      return mappedData?.[param];
    },
    [mappedData],
  );

  return { getParam, ...queryResult, isLoading, data };
};

type Key = keyof ProjectParams;

function getProjectParam(property: Key): Promise<ProjectParams[Key] | null | undefined>;
function getProjectParam<T extends Key>(properties: T[]): Promise<Pick<ProjectParams, T>>;

async function getProjectParam<T extends Key>(
  properties: T | T[],
): Promise<ProjectParams[T] | Pick<ProjectParams, T> | null | undefined> {
  const projectUuid = parametersStore.get('project_uuid') || undefined;
  const participationUuid = parametersStore.get('participation_uuid') || undefined;
  const isPreview = parametersStore.get('is_preview') === '1' || !participationUuid;

  const data = await getProjectDetailsCached({
    projectUuid,
    participationUuid,
    isPreview,
  });

  const mappedData = mapProjectParams(data);

  if (typeof properties === 'string') {
    return mappedData?.[properties];
  }

  return properties.reduce((result, key) => {
    const value = mappedData?.[key];
    if (value !== undefined) {
      result[key] = value;
    }
    return result;
  }, {} as ProjectParams);
}

export { useProjectParams, getProjectParam };
